import { FC } from "react";
import {
  Box,
  Typography,
  Stack,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { tokens } from "@/tokens";
import type { FilterOperator, EntrySummaryFiltersState } from "./EntrySummaryFilters";

const filterOperators: { value: FilterOperator; label: string }[] = [
  { value: "contains", label: "Contains" },
  { value: "equals", label: "Equals" },
  { value: "starts_with", label: "Starts with" },
  { value: "ends_with", label: "Ends with" },
  { value: "not_contains", label: "Does not contain" },
  { value: "not_equals", label: "Does not equal" },
];

interface TextFilterWithOperatorInputProps {
  label: string;
  filterKey: "filerCode" | "commercialDescription" | "entrySummaryNumber";
  placeholder: string;
  localFilters: EntrySummaryFiltersState;
  setLocalFilters: React.Dispatch<React.SetStateAction<EntrySummaryFiltersState>>;
}

const TextFilterWithOperatorInput: FC<TextFilterWithOperatorInputProps> = ({
  label,
  filterKey,
  placeholder,
  localFilters,
  setLocalFilters,
}) => (
  <Box>
    <Typography
      sx={{
        mb: 1,
        color: tokens.text.primary,
        fontWeight: tokens.fontWeight.semibold,
        fontSize: tokens.fontSize.base,
      }}
    >
      {label}
    </Typography>
    <Stack spacing={1}>
      <FormControl size="small" fullWidth>
        <Select
          value={localFilters[filterKey].operator}
          onChange={(e) =>
            setLocalFilters((prev) => ({
              ...prev,
              [filterKey]: {
                ...prev[filterKey],
                operator: e.target.value as FilterOperator,
              },
            }))
          }
          sx={{
            backgroundColor: tokens.bg.surface,
            "& .MuiSelect-select": {
              py: 1,
              fontSize: tokens.fontSize.base,
            },
          }}
        >
          {filterOperators.map((op) => (
            <MenuItem key={op.value} value={op.value} sx={{ fontSize: tokens.fontSize.base }}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={localFilters[filterKey].value}
        onChange={(e) =>
          setLocalFilters((prev) => ({
            ...prev,
            [filterKey]: {
              ...prev[filterKey],
              value: e.target.value,
            },
          }))
        }
        sx={{
          backgroundColor: tokens.bg.surface,
          "& .MuiInputBase-input": {
            fontSize: tokens.fontSize.base,
          },
        }}
        InputProps={{
          endAdornment: localFilters[filterKey].value ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    [filterKey]: { ...prev[filterKey], value: "" },
                  }))
                }
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />
    </Stack>
  </Box>
);

export default TextFilterWithOperatorInput;
